import {
    IonBackButton,
    IonButton,
    IonIcon,
    IonButtons,
    IonContent,
    IonHeader,
    IonPage,
    IonTextarea,
    IonTitle,
    IonToolbar,
    useIonRouter,
    useIonViewDidEnter,
    IonInput,
    IonMenuButton
} from '@ionic/react';
import { useState, useEffect } from 'react';
import { GithubPicker, ChromePicker } from 'react-color';
import axios from 'axios';
import { addOutline, sparklesOutline, starOutline, flashOutline, arrowForwardOutline, checkmarkCircleOutline } from 'ionicons/icons';
import { isOrganizationMember } from '../helpers/organizationUtils';
import API_CONFIG from '../config/api';
import './Add.css';
import Loader from '../components/Loader';
import GlobalHelp from '../components/GlobalHelp';

const Add = () => {
    const router = useIonRouter();
    const [loading, setLoading] = useState(false);
    const [showPicker, setShowPicker] = useState(false);
    const [pollQuestion, setPollQuestion] = useState("");
    const [pollColor, setPollColor] = useState("#22c55e");
    const [pollAnswers, setPollAnswers] = useState([]);
    const [maxVotes, setMaxVotes] = useState(1);
    const [isOrgMember, setIsOrgMember] = useState(false);


    const [pollDays, setPollDays] = useState(0);
    const [pollHours, setPollHours] = useState(0);
    const [pollMins, setPollMins] = useState(0);

    // AI Generation State
    const [aiPrompt, setAiPrompt] = useState("");
    const [pollCount, setPollCount] = useState(1);
    const [generatedPolls, setGeneratedPolls] = useState([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isAiMode, setIsAiMode] = useState(false);
    const [isEnhancing, setIsEnhancing] = useState(false);

    const pickerColors = [
        "#22c55e",
        "#16a34a",
        "#3b82f6",
        "#8b5cf6",
        "#f59e0b",
        "#ef4444",
        "#ec4899",
        "#06b6d4",
        "#84cc16",
        "#f97316",
        "#6366f1",
        "#64748b"
    ];

    useIonViewDidEnter(() => {
        setShowPicker(false);
        setPollQuestion("");
        setPollColor("#22c55e");
        setPollAnswers([]);
        setPollDays(0);
        setPollHours(0);
        setPollMins(0);

        // Check if user belongs to an organization
        const orgMember = isOrganizationMember();
        console.log('Organization check:', {
            organization_id: sessionStorage.getItem('organization_id'),
            user_id: sessionStorage.getItem('user_id'),
            isOrgMember: orgMember
        });
        setIsOrgMember(orgMember);
    });

    const handleAdd = async () => {
        const timeLeftDays = pollDays !== 0 && pollDays !== "" ? `${pollDays} days, ` : "";
        const timeLeftHours = pollHours !== 0 && pollHours !== "" ? `${pollHours} hours, ` : "";
        const timeLeftMins = pollMins !== 0 && pollMins !== "" ? `${pollMins} mins` : "";

        const timeLeft = `${timeLeftDays}${timeLeftHours}${timeLeftMins}`;

        const poll = {
            user_id: sessionStorage.getItem('user_id') || "nill",
            question: pollQuestion,
            color: pollColor,
            timeLeft,
            max_votes_per_user: maxVotes,
            answers: pollAnswers
        };


        setLoading(true);
        const safetyTimeout = setTimeout(() => setLoading(false), 12000);

        try {
            const response = await axios.post(API_CONFIG.ADD_POLL, poll, { timeout: 8000 });
            console.log('Poll added:', response.data);

            // Navigate to view page and trigger a refresh
            router.push("/page/view");

            // Also notify other components to refresh their data
            window.dispatchEvent(new CustomEvent('pollCreated', { detail: response.data.poll }));
        } catch (error) {
            console.error('Error adding poll:', error);

            // Show specific error message from backend
            const errorMessage = error.response?.data?.error || 'Failed to create poll. Please try again.';
            alert(errorMessage);
        } finally {
            clearTimeout(safetyTimeout);
            setLoading(false);
        }
    }

    const addAnswer = () => {
        const answer = {
            id: Date.now(),
            answer: "",
            votes: 0,
            voted: false,
            percent: 0
        };

        setPollAnswers(prev => [...prev, answer]);
    }

    const removeAnswer = (answerId) => {
        const newAnswers = pollAnswers.filter((p) => p.id !== answerId);
        setPollAnswers(newAnswers);
    }

    const handleAnswerChange = (e, index) => {
        const newAnswers = [...pollAnswers];
        newAnswers[index].answer = e.target.value;
        setPollAnswers(newAnswers);
    }

    const handleAiGenerate = async () => {
        if (!aiPrompt.trim()) {
            alert("Please enter a prompt first");
            return;
        }
        const userId = sessionStorage.getItem('user_id') || localStorage.getItem('userId');
        setIsGenerating(true);
        try {
            const response = await axios.post(API_CONFIG.GENERATE_POLL_AI, {
                prompt: aiPrompt,
                count: parseInt(pollCount),
                user_id: userId
            });

            if (response.data.success) {
                setGeneratedPolls(response.data.polls);
            } else {
                alert(response.data.error || "Failed to generate polls");
            }
        } catch (error) {
            console.error('AI Error:', error);
            alert("Error generating polls with AI");
        } finally {
            setIsGenerating(false);
        }
    }

    const handleAiEnhance = async () => {
        if (!aiPrompt.trim()) {
            alert("Please enter a raw idea first");
            return;
        }
        const userId = sessionStorage.getItem('user_id') || localStorage.getItem('userId');
        setIsEnhancing(true);
        try {
            const response = await axios.post(API_CONFIG.GENERATE_POLL_AI, {
                prompt: aiPrompt,
                action: 'enhance',
                user_id: userId
            });

            if (response.data.success) {
                setAiPrompt(response.data.enhancedPrompt);
            } else {
                alert(response.data.error || "Failed to enhance prompt");
            }
        } catch (error) {
            console.error('Enhance Error:', error);
            alert("Error enhancing prompt with AI");
        } finally {
            setIsEnhancing(false);
        }
    }

    const importPoll = (poll) => {
        setPollQuestion(poll.question);
        setPollColor(poll.color);
        const formattedAnswers = poll.answers.map((ans, idx) => ({
            id: Math.floor(Date.now() / 1000) + idx,
            answer: ans,
            votes: 0,
            voted: false,
            percent: 0
        }));
        setPollAnswers(formattedAnswers);
        // Switch to manual mode for final review
        setIsAiMode(false);
        setGeneratedPolls([]);
    }

    const saveAllGenerated = async () => {
        setLoading(true);
        const safetyTimeout = setTimeout(() => setLoading(false), 30000); // Longer timeout for bulk operations

        try {
            const userId = sessionStorage.getItem('user_id');
            const results = [];

            for (const poll of generatedPolls) {
                const timeLeftDays = pollDays !== 0 && pollDays !== "" ? `${pollDays} days, ` : "";
                const timeLeftHours = pollHours !== 0 && pollHours !== "" ? `${pollHours} hours, ` : "";
                const timeLeftMins = pollMins !== 0 && pollMins !== "" ? `${pollMins} mins` : "";
                const timeLeft = `${timeLeftDays}${timeLeftHours}${timeLeftMins}` || "7 days";

                const submitData = {
                    user_id: userId,
                    question: poll.question,
                    color: poll.color,
                    timeLeft: timeLeft,
                    max_votes_per_user: maxVotes,
                    answers: poll.answers.map((ans, idx) => ({

                        id: Math.floor(Date.now() / 1000) + idx + Math.floor(Math.random() * 1000),
                        answer: ans,
                        votes: 0,
                        voted: false,
                        percent: 0
                    }))
                };
                const res = await axios.post(API_CONFIG.ADD_POLL, submitData, { timeout: 8000 });
                results.push(res.data);
            }

            alert(`Successfully created ${results.length} polls!`);
            router.push("/page/view");
        } catch (error) {
            console.error('Bulk save error:', error);
            alert("Error saving generated polls");
        } finally {
            clearTimeout(safetyTimeout);
            setLoading(false);
        }
    }

    const isAdmin = () => {
        const roleId = sessionStorage.getItem('role_id') || localStorage.getItem('roleId');
        return roleId === '1' || roleId === '2';
    }

    return (
        <IonPage className="add-page">
            <IonHeader className="ion-no-border" translucent={true}>
                <IonToolbar className="add-header-toolbar frosted-header">
                    <IonButtons slot="start">
                        <IonBackButton text="Back" className="back-button" />
                    </IonButtons>
                    <IonTitle className="add-page-title">
                        <div className="title-container">
                            <span className="title-text">Create New Poll</span>
                        </div>
                    </IonTitle>

                    <IonButtons slot="end">
                        <GlobalHelp inline={true} />
                        <IonMenuButton />
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="add-content">
                {loading && <Loader message="Architecting Your Poll..." />}
                <div className="add-container">
                    {/* Creation Mode Toggle */}
                    {isAdmin() && (isOrgMember || sessionStorage.getItem('role_id') === '1') && (
                        <div className="mode-toggle-container mb-6">
                            <div className="mode-toggle-label mb-2">How would you like to create your poll?</div>
                            <div className="mode-toggle-buttons">
                                <IonButton
                                    fill={!isAiMode ? "solid" : "outline"}
                                    className={`toggle-button ${!isAiMode ? 'active' : ''}`}
                                    onClick={() => setIsAiMode(false)}
                                >
                                    <IonIcon icon={addOutline} slot="start" />
                                    Manual Creation
                                </IonButton>
                                <IonButton
                                    fill={isAiMode ? "solid" : "outline"}
                                    className={`toggle-button ai-btn ${isAiMode ? 'active' : ''}`}
                                    onClick={() => setIsAiMode(true)}
                                >
                                    <IonIcon icon={sparklesOutline} slot="start" />
                                    Smart AI Assistant
                                </IonButton>
                            </div>
                        </div>
                    )}

                    {/* AI Poll Assistant Section (Admins Only) */}
                    {isAiMode && isAdmin() && (isOrgMember || sessionStorage.getItem('role_id') === '1') && (
                        <div className="form-section ai-assistant-section mb-8">
                            <div className="section-header ai-header">
                                <div className="flex items-center gap-2">
                                    <div className="ai-icon-pulse">
                                        <IonIcon icon={sparklesOutline} className="text-2xl text-slate-100" />
                                    </div>
                                    <h2 className="section-title ai-title">Smart Poll Assistant</h2>
                                </div>
                            </div>

                            <div className="ai-input-container">
                                <div className="prompt-wrapper relative">
                                    <IonTextarea
                                        className="ai-prompt-input"
                                        rows={4}
                                        value={aiPrompt}
                                        onIonInput={e => setAiPrompt(e.detail.value)}
                                        placeholder="e.g. Create a poll to ask employees about their preferred hybrid work schedule..."
                                    />
                                    <IonButton
                                        className="enhance-prompt-button"
                                        fill="clear"
                                        onClick={handleAiEnhance}
                                        disabled={isEnhancing || !aiPrompt.trim()}
                                        title="Enhance Prompt"
                                    >
                                        {isEnhancing ? (
                                            <Loader message="Refining..." hideBg={true} />
                                        ) : (
                                            <IonIcon icon={starOutline} />
                                        )}
                                        <span>Enhance</span>
                                    </IonButton>
                                </div>

                                {/* AI-Specific Poll Duration Section */}
                                <div className="ai-duration-box mt-4 mb-4">
                                    <h3 className="text-sm font-semibold text-slate-700 mb-2">Poll Duration</h3>
                                    <div className="duration-container ai-duration-grid">
                                        <div className="duration-input">
                                            <label className="duration-label text-xs">Days</label>
                                            <input
                                                type="number"
                                                className="duration-field"
                                                value={pollDays}
                                                onChange={(e) => setPollDays(e.target.value)}
                                                min="0"
                                                max="30"
                                            />
                                        </div>
                                        <div className="duration-input">
                                            <label className="duration-label text-xs">Hours</label>
                                            <input
                                                type="number"
                                                className="duration-field"
                                                value={pollHours}
                                                onChange={(e) => setPollHours(e.target.value)}
                                                min="0"
                                                max="23"
                                            />
                                        </div>
                                        <div className="duration-input">
                                            <label className="duration-label text-xs">Minutes</label>
                                            <input
                                                type="number"
                                                className="duration-field"
                                                value={pollMins}
                                                onChange={(e) => setPollMins(e.target.value)}
                                                min="0"
                                                max="59"
                                            />
                                        </div>
                                    </div>
                                </div>

                            </div>

                            {/* Generated Polls Preview Section */}
                            {generatedPolls.length > 0 && (
                                <div className="generated-polls-preview mt-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-lg font-bold text-indigo-900">Generated Results ({generatedPolls.length})</h3>
                                        {generatedPolls.length > 1 && (
                                            <IonButton fill="clear" color="secondary" onClick={saveAllGenerated}>
                                                Bulk Save All
                                            </IonButton>
                                        )}
                                    </div>
                                    <div className="polls-preview-list">
                                        {generatedPolls.map((poll, idx) => (
                                            <div key={idx} className="preview-card" style={{ borderLeft: `6px solid ${poll.color}` }}>
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <p className="preview-question font-semibold text-gray-800">{poll.question}</p>
                                                        <div className="preview-options flex flex-wrap gap-2 mt-2">
                                                            {poll.answers.map((ans, aidx) => (
                                                                <span key={aidx} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                                                    {ans}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <IonButton fill="clear" onClick={() => importPoll(poll)}>
                                                        <IonIcon icon={arrowForwardOutline} />
                                                    </IonButton>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Vote Settings Section for AI Mode */}
                            <div className="form-section mt-6">
                                <div className="section-header">
                                    <h2 className="section-title text-indigo-900">Vote Settings</h2>
                                    <p className="section-subtitle">How many options can people vote for in these polls?</p>
                                </div>

                                <div className="vote-limit-container">
                                    <div className="limit-toggle-buttons">
                                        <button
                                            type="button"
                                            className={`limit-btn ${maxVotes === 1 ? 'active' : ''}`}
                                            onClick={() => setMaxVotes(1)}
                                        >
                                            <div className="limit-icon">1</div>
                                            <div className="limit-info">
                                                <span className="limit-name">Single Vote</span>
                                                <span className="limit-desc">One option only</span>
                                            </div>
                                            {maxVotes === 1 && <IonIcon icon={checkmarkCircleOutline} className="check-icon" />}
                                        </button>
                                        <button
                                            type="button"
                                            className={`limit-btn ${maxVotes === 2 ? 'active' : ''}`}
                                            onClick={() => setMaxVotes(2)}
                                        >
                                            <div className="limit-icon">2</div>
                                            <div className="limit-info">
                                                <span className="limit-name">Double Vote</span>
                                                <span className="limit-desc">Up to two options</span>
                                            </div>
                                            {maxVotes === 2 && <IonIcon icon={checkmarkCircleOutline} className="check-icon" />}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* AI-Specific Poll Theme Section */}
                            <div className="form-section mt-6">
                                <div className="section-header">
                                    <h2 className="section-title text-indigo-900">Poll Theme</h2>
                                    <p className="section-subtitle">Choose a color for your AI-generated polls</p>
                                </div>
                                <div className="color-section">
                                    <IonButton
                                        className="color-picker-button"
                                        onClick={() => setShowPicker(!showPicker)}
                                        style={{ '--background': pollColor }}
                                    >
                                        <IonIcon icon={addOutline} slot="start" />
                                        Choose Color
                                    </IonButton>
                                    {showPicker && (
                                        <div className="color-picker-container">
                                            <div className="color-picker-wrapper">
                                                <GithubPicker
                                                    colors={pickerColors}
                                                    color={pollColor}
                                                    onChange={color => setPollColor(color.hex)}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center justify-between mt-8 mb-4 gap-4 p-4 bg-slate-50 rounded-xl border border-dashed border-indigo-200/50">
                                <div className="flex items-center gap-3">
                                    <label className="text-sm font-semibold text-indigo-900/70">Count:</label>
                                    <input
                                        type="number"
                                        className="poll-count-field"
                                        value={pollCount}
                                        onChange={(e) => setPollCount(Math.min(10, Math.max(1, e.target.value)))}
                                        min="1"
                                        max="10"
                                    />
                                </div>
                                <IonButton
                                    className="ai-generate-button"
                                    onClick={handleAiGenerate}
                                    style={{ '--box-shadow': '0 4px 15px rgba(99, 102, 241, 0.3)' }}
                                    disabled={isGenerating || isEnhancing || !aiPrompt.trim()}
                                >
                                    {isGenerating ? (
                                        <Loader message="Architecting..." hideBg={true} />
                                    ) : (
                                        <>
                                            <IonIcon icon={flashOutline} slot="start" />
                                            Generate Polls
                                        </>
                                    )}
                                </IonButton>
                            </div>
                        </div>
                    )}



                    {/* Organization Membership Warning */}
                    {!isOrgMember && sessionStorage.getItem('role_id') !== '1' && (
                        <div style={{
                            backgroundColor: '#fff3cd',
                            border: '1px solid #ffeaa7',
                            borderRadius: '8px',
                            padding: '12px 16px',
                            margin: '16px 0',
                            color: '#856404'
                        }}>
                            <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 'bold' }}>
                                Organization Membership Required
                            </h3>
                            <p style={{ margin: '0', fontSize: '14px' }}>
                                Only organization members can create polls. Please join an organization first to create polls.
                            </p>
                        </div>
                    )}

                    {/* Manual Form Section */}
                    {!isAiMode && (
                        <div className="add-form">
                            {/* Poll Color Section */}
                            <div className="form-section mt-4">
                                <div className="section-header">
                                    <h2 className="section-title">Poll Theme</h2>
                                    <p className="section-subtitle">Choose a color that represents your poll</p>
                                </div>

                                <div className="color-section">
                                    <IonButton
                                        className="color-picker-button"
                                        onClick={() => setShowPicker(!showPicker)}
                                        style={{ '--background': pollColor }}
                                    >
                                        <IonIcon icon={addOutline} slot="start" />
                                        Choose Color
                                    </IonButton>

                                    {showPicker && (
                                        <div className="color-picker-container">
                                            <div className="color-picker-wrapper">
                                                <GithubPicker
                                                    colors={pickerColors}
                                                    color={pollColor}
                                                    onChange={color => setPollColor(color.hex)}
                                                />
                                                <div className="advanced-picker">
                                                    <ChromePicker
                                                        color={pollColor}
                                                        onChange={color => setPollColor(color.hex)}
                                                        disableAlpha={true}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Poll Question Section */}
                            <div className="form-section">
                                <div className="section-header">
                                    <h2 className="section-title">Poll Question</h2>
                                    <p className="section-subtitle">What would you like to ask?</p>
                                </div>

                                <div className="question-input-container">
                                    <IonTextarea
                                        className="question-input"
                                        rows={3}
                                        value={pollQuestion}
                                        onIonInput={e => setPollQuestion(e.detail.value)}
                                        placeholder="Enter your poll question here..."
                                    />
                                </div>
                            </div>

                            {/* Poll Duration Section */}
                            <div className="form-section">
                                <div className="section-header">
                                    <h2 className="section-title">Poll Duration</h2>
                                    <p className="section-subtitle">How long should this poll remain active?</p>
                                </div>

                                <div className="duration-container">
                                    <div className="duration-input">
                                        <label className="duration-label">Days</label>
                                        <input
                                            type="number"
                                            className="duration-field"
                                            value={pollDays}
                                            onChange={(e) => setPollDays(e.target.value)}
                                            min="0"
                                            max="30"
                                        />
                                    </div>
                                    <div className="duration-input">
                                        <label className="duration-label">Hours</label>
                                        <input
                                            type="number"
                                            className="duration-field"
                                            value={pollHours}
                                            onChange={(e) => setPollHours(e.target.value)}
                                            min="0"
                                            max="23"
                                        />
                                    </div>
                                    <div className="duration-input">
                                        <label className="duration-label">Minutes</label>
                                        <input
                                            type="number"
                                            className="duration-field"
                                            value={pollMins}
                                            onChange={(e) => setPollMins(e.target.value)}
                                            min="0"
                                            max="59"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Vote Settings Section */}
                            <div className="form-section">
                                <div className="section-header">
                                    <h2 className="section-title">Vote Settings</h2>
                                    <p className="section-subtitle">How many options can each person vote for?</p>
                                </div>

                                <div className="vote-limit-container">
                                    <div className="limit-toggle-buttons">
                                        <button
                                            type="button"
                                            className={`limit-btn ${maxVotes === 1 ? 'active' : ''}`}
                                            onClick={() => setMaxVotes(1)}
                                        >
                                            <div className="limit-icon">1</div>
                                            <div className="limit-info">
                                                <span className="limit-name">Single Vote</span>
                                                <span className="limit-desc">Users pick only one option</span>
                                            </div>
                                            {maxVotes === 1 && <IonIcon icon={checkmarkCircleOutline} className="check-icon" />}
                                        </button>
                                        <button
                                            type="button"
                                            className={`limit-btn ${maxVotes === 2 ? 'active' : ''}`}
                                            onClick={() => setMaxVotes(2)}
                                        >
                                            <div className="limit-icon">2</div>
                                            <div className="limit-info">
                                                <span className="limit-name">Double Vote</span>
                                                <span className="limit-desc">Users can pick up to two options</span>
                                            </div>
                                            {maxVotes === 2 && <IonIcon icon={checkmarkCircleOutline} className="check-icon" />}
                                        </button>
                                    </div>
                                </div>
                            </div>


                            {/* Poll Answers Section */}
                            <div className="form-section">
                                <div className="section-header">
                                    <h2 className="section-title">Poll Options</h2>
                                    <p className="section-subtitle">Add the possible answers for your poll</p>
                                </div>

                                <div className="answers-container">
                                    {pollAnswers.map((answer, index) => (
                                        <div key={answer.id} className="answer-item">
                                            <div className="answer-input-wrapper">
                                                <label className="answer-label">Option {index + 1}</label>
                                                <div className="answer-input-container">
                                                    <input
                                                        type="text"
                                                        className="answer-input"
                                                        value={answer.answer}
                                                        onChange={(e) => handleAnswerChange(e, index)}
                                                        placeholder="Enter answer option..."
                                                    />
                                                    <IonButton
                                                        className="remove-answer-button"
                                                        onClick={() => removeAnswer(answer.id)}
                                                        fill="clear"
                                                    >
                                                        <IonIcon icon={addOutline} style={{ transform: 'rotate(45deg)' }} />
                                                    </IonButton>
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    {pollAnswers.length === 0 && (
                                        <div className="empty-answers">
                                            <div className="empty-icon">📝</div>
                                            <h3 className="empty-title">No options added yet</h3>
                                            <p className="empty-message">Add some options for people to choose from</p>
                                        </div>
                                    )}

                                    <IonButton
                                        className="add-answer-button"
                                        onClick={addAnswer}
                                        fill="outline"
                                    >
                                        <IonIcon icon={addOutline} slot="start" />
                                        Add Option
                                    </IonButton>
                                </div>
                            </div>

                            {/* Submit Section */}
                            <div className="manual-submit-row flex items-center justify-between mt-8 mb-4 gap-4 p-4 bg-white/60 rounded-xl border border-dashed border-indigo-100/50">
                                <div className="flex-1"></div>
                                <IonButton
                                    className="submit-button"
                                    onClick={handleAdd}
                                    style={{ margin: 0, '--box-shadow': '0 4px 15px rgba(79, 70, 229, 0.2)' }}
                                    disabled={!pollQuestion.trim() || pollAnswers.length < 2}
                                >
                                    <IonIcon icon={flashOutline} slot="start" />
                                    Create Poll
                                </IonButton>
                            </div>
                        </div>
                    )}
                </div>
            </IonContent>
        </IonPage>
    );
};

export default Add;